// screen: Registered viewers — privacy requests (change viewer-crm)
// Registered viewers — privacy requests (change viewer-crm, design §4.4). The queue by due date (received +
// the tenant's deadline, 30 days by default), requests logged by staff (e-mail / phone), the viewer's own exports and
// deletions as completed self-service entries; completing a deletion deletes the account. viewers:manage.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Plus } from 'lucide-react';
import type { PrivacyKind, PrivacyRequest } from '@viewstream/api-client';
import { Badge, Button, Dialog, DialogContent, Input, Select, Skeleton, Switch, useToast } from '@viewstream/ui';
import { fmtDate } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { problemText } from '~/app/ingest';
import { useCreatePrivacyRequest, usePrivacyRequests, useUpdatePrivacyRequest } from '~/app/viewers';
import { useRequestKindLabel, useRequestStatusLabel, ViewersHeader, VIEWERS_MANAGE, VIEWERS_PII } from '~/app/ViewersNav';

export const Route = createFileRoute('/_layout/t/$slug/viewers/requests')({ component: Screen });

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  React.useEffect(() => { document.title = appTitle(t`Privacy requests`); }, [t]);
  const [all, setAll] = React.useState(false);
  const allowed = can(scopes, VIEWERS_MANAGE);
  const reqs = usePrivacyRequests(tenant.slug, { all }, allowed);
  const create = useCreatePrivacyRequest(tenant.slug);
  const update = useUpdatePrivacyRequest(tenant.slug);
  const kind = useRequestKindLabel();
  const status = useRequestStatusLabel();
  const [form, setForm] = React.useState<{ account_id: string; contact_email: string; kind: PrivacyKind; channel: 'email' | 'phone' | 'other'; note: string } | null>(null);
  const [reject, setReject] = React.useState<PrivacyRequest | null>(null);
  const [reason, setReason] = React.useState('');
  const fail = (e: unknown) => toast.push({ title: t`Something went wrong`, description: problemText(e), tone: 'danger' });
  const d = (iso: string | null) => (iso ? fmtDate(iso, { locale, tz }) : '—');
  if (!allowed) return <p className="p-6 text-sm">{t`Privacy requests are handled by Admins and Owners.`}</p>;
  const channelLabel: Record<string, string> = { self_service: t`By the viewer, in the account`, email: t`E-mail`, phone: t`Phone`, other: t`Other` };

  return (
    <div>
      <ViewersHeader tab="requests">
        <Button onClick={() => setForm({ account_id: '', contact_email: '', kind: 'access', channel: 'email', note: '' })} data-testid="request-new"><Plus className="size-4" aria-hidden /> {t`Log a request`}</Button>
      </ViewersHeader>
      <p className="mb-3 max-w-3xl text-sm text-fg-muted">{t`Viewers may ask to see, export, correct or delete their data, or to stop marketing e-mails. Answer by the due date; reminders arrive 7 days and 1 day before it. Requests a viewer makes in their own account appear here as completed.`}</p>
      <label className="mb-3 flex items-center gap-2 text-sm"><Switch checked={all} onCheckedChange={setAll} /> {t`Show closed requests`}</label>
      {reqs.isPending ? <Skeleton className="h-24 w-full" /> : (reqs.data ?? []).length === 0 ? <p className="text-sm text-fg-muted">{t`No open requests.`}</p> : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="privacy-requests">
            <thead className="bg-surface-2 text-fg-muted"><tr>
              <th className="px-3 py-2 text-start font-medium">{t`Request`}</th>
              <th className="px-3 py-2 text-start font-medium">{t`Viewer`}</th>
              <th className="px-3 py-2 text-start font-medium">{t`Received`}</th>
              <th className="px-3 py-2 text-start font-medium">{t`Due`}</th>
              <th className="px-3 py-2 text-start font-medium">{t`Status`}</th>
              <th className="px-3 py-2" />
            </tr></thead>
            <tbody className="divide-y divide-border">
              {reqs.data!.map((r) => (
                <tr key={r.id} data-request-id={r.id}>
                  <td className="px-3 py-2">{kind(r.kind)}<div className="text-xs text-fg-muted">{channelLabel[r.channel]}{r.note ? ` · ${r.note}` : ''}</div></td>
                  <td className="px-3 py-2">{r.account_id ? (can(scopes, VIEWERS_PII)
                    ? <Link to={'/t/$slug/viewers/$viewerId' as never} params={{ slug: tenant.slug, viewerId: r.account_id } as never} className="text-primary hover:underline">{t`Open`}</Link>
                    : <bdi dir="ltr" className="font-mono text-xs">{r.account_id.slice(0, 8)}</bdi>) : t`No account (contact e-mail)`}</td>
                  <td className="px-3 py-2">{d(r.received_at)}</td>
                  <td className="px-3 py-2">{d(r.due_at)} {r.overdue ? <Badge tone="danger">{t`Overdue`}</Badge> : null}</td>
                  <td className="px-3 py-2"><Badge tone={r.status === 'done' ? 'ok' : r.status === 'rejected' ? 'neutral' : 'warn'}>{status(r.status)}</Badge>{r.reason ? <div className="text-xs text-fg-muted">{r.reason}</div> : null}</td>
                  <td className="px-3 py-2 text-end">
                    {r.status === 'new' ? <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: r.id, status: 'in_progress' }, { onError: fail })}>{t`Start`}</Button> : null}
                    {r.status === 'new' || r.status === 'in_progress' ? (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => update.mutate({ id: r.id, status: 'done' }, { onError: fail })}>{r.kind === 'delete' && r.account_id ? t`Delete the account and complete` : t`Complete`}</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setReason(''); setReject(r); }}>{t`Reject…`}</Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={form !== null} onOpenChange={(o) => { if (!o) setForm(null); }}>
        {form ? (
          <DialogContent title={t`Log a privacy request`} closeLabel={t`Close`} description={t`For a request received by e-mail or phone. Give the viewer id, or the contact e-mail when there is no account (only a fingerprint of the address is kept).`}>
            <div className="space-y-2">
              <Select ariaLabel={t`Request`} value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v as PrivacyKind })}
                options={(['access', 'export', 'correct', 'delete', 'object_marketing'] as PrivacyKind[]).map((k) => ({ value: k, label: kind(k) }))} />
              <Select ariaLabel={t`Received by`} value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v as 'email' | 'phone' | 'other' })}
                options={[{ value: 'email', label: t`E-mail` }, { value: 'phone', label: t`Phone` }, { value: 'other', label: t`Other` }]} />
              <Input dir="ltr" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value.trim() })} placeholder={t`Viewer id`} aria-label={t`Viewer id`} />
              <Input dir="ltr" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder={t`Contact e-mail (no account)`} aria-label={t`Contact e-mail`} />
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t`Note (optional)`} aria-label={t`Note`} />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setForm(null)}>{t`Cancel`}</Button>
              <Button disabled={create.isPending || (!form.account_id && !form.contact_email)} onClick={() => create.mutate({
                kind: form.kind, channel: form.channel, note: form.note || undefined, account_id: form.account_id || undefined, contact_email: form.account_id ? undefined : form.contact_email || undefined,
              }, { onSuccess: () => setForm(null), onError: fail })}>{t`Save`}</Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>

      <Dialog open={reject !== null} onOpenChange={(o) => { if (!o) setReject(null); }}>
        {reject ? (
          <DialogContent title={t`Reject the request?`} closeLabel={t`Close`} description={t`The reason is kept with the request.`}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t`Reason`} aria-label={t`Reason`} />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setReject(null)}>{t`Cancel`}</Button>
              <Button variant="danger" disabled={!reason.trim()} onClick={() => update.mutate({ id: reject.id, status: 'rejected', reason }, { onSuccess: () => setReject(null), onError: fail })}>{t`Reject`}</Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
